<div class="h-full flex flex-col">
    <div class="p-3 flex-1 flex overflow-auto h-full">
        <div
            [ngClass]="canView() ? 'flex flex-col xl:flex-row gap-4 h-full w-full max-w-7xl mx-auto' : 'flex justify-center w-full h-full max-w-2xl mx-auto'">
            <div class="flex flex-col flex-1 xl:w-1/2 h-full">
                <p-card
                    class="border rounded-base organization-form-card flex-shrink-0 flex flex-col gap-2 mb-4 xl:mb-0 h-full">
                    <div class="flex flex-row flex-wrap justify-end gap-2 flex-shrink-0">
                        @if(canEdit()){
                        <app-button variant="outlined" severity="contrast" (clicked)="enterEditMode()"
                            [label]="nonEditable ? 'Edit' : 'Cancel'" [icon]="nonEditable ? 'pencil' : 'x'"
                            [iconSource]="'lucide'" class="w-full sm:w-auto" />
                        }
                    </div>
                    <form [formGroup]="organizationForm" (ngSubmit)="onSubmitOrganization()"
                        class="flex-1 overflow-auto flex flex-col gap-4">
                        <div class="form-field flex flex-col gap-2">
                            <label for="companyLogo">Company Logo</label>
                            <div class="flex flex-col gap-2">
                                <div class="flex items-center gap-4">
                                    <!-- CASE 1: Logo exists and not marked for removal - Show logo with edit/delete overlay on hover (edit mode only) -->
                                    <div *ngIf="displayLogoUrl && !removeLogo"
                                        class="relative h-[6.25rem] w-[6.25rem] rounded-full overflow-hidden border-2 border-gray-200 group">
                                        <img [src]="displayLogoUrl" alt="Company Logo"
                                            class="h-full w-full object-cover">

                                        <!-- Edit/Delete icons overlay - visible on hover in edit mode -->
                                        <div *ngIf="!nonEditable"
                                            class="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-3">
                                            <app-button (clicked)="triggerLogoFileInput()" variant="outlined"
                                                severity="contrast" toolTip="Edit Logo" [icon]="'pencil'"
                                                [iconSource]="'lucide'" />
                                            <app-button (clicked)="removeSelectedLogo()" variant="outlined"
                                                severity="danger" toolTip="Delete Logo" [icon]="'trash-2'"
                                                [iconSource]="'lucide'" />
                                        </div>
                                    </div>

                                    <!-- CASE 2: No logo OR logo removed - Show "Add Logo" button (edit mode only) -->
                                    <div *ngIf="(removeLogo || !displayLogoUrl) && !nonEditable"
                                        class="h-[6.25rem] w-[6.25rem] bg-slate-100 rounded-full flex items-center justify-center text-center text-sm text-gray-800 hover:cursor-pointer transform transition-transform duration-200 hover:scale-105 border-2 border-dashed border-gray-300"
                                        (click)="triggerLogoFileInput()">
                                        <div class="flex flex-col items-center gap-1">
                                            <app-icon type="plus" source="lucide" class="text-lg" />
                                            <span class="text-xs font-medium">Add Logo</span>
                                        </div>
                                    </div>

                                    <!-- CASE 3: No logo in view mode - Show "No Logo" placeholder (non-editable/view mode) -->
                                    <div *ngIf="!displayLogoUrl && !removeLogo && nonEditable"
                                        class="h-[6.25rem] w-[6.25rem] bg-slate-100 rounded-full flex items-center justify-center text-center text-sm text-gray-400 border-2 border-gray-200">
                                        <div class="flex flex-col items-center gap-1">
                                            <app-icon type="image" source="lucide" class="text-lg" />
                                            <span class="text-xs">No Logo</span>
                                        </div>
                                    </div>
                                </div>
                                <!-- Error message for logo size -->
                                <div *ngIf="logoSizeError" class="text-red-500 text-sm">
                                    {{ logoSizeError }}
                                </div>
                            </div>

                            <input #logoFileInput type="file" accept="image/jpeg,image/jpg,image/png,image/gif"
                                class="hidden" (change)="onLogoSelect($event)">
                        </div>

                        <div class="form-field flex flex-col gap-2">
                            <label for="companyName">Company Name <span class="text-danger">*</span></label>
                            <app-input id="companyName" formControlName="companyName"
                                (input)="onCompanyNameChange($event)" placeholder="Your Company" />
                            <div *ngIf="organizationForm.controls['companyName'].errors && organizationForm.controls['companyName'].touched"
                                class="text-danger">
                                <small>{{"*Company Name is Required" | titlecase}}</small>
                            </div>
                        </div>

                        <div class="flex flex-col sm:flex-row gap-2">
                            <div class="form-field flex flex-col gap-2 flex-1 w-full sm:w-auto">
                                <label for="companySize">Company Size</label>
                                <app-select [options]="noOfEmployees" [config]="companySizeConfig"
                                    formControlName="companySize" />
                                <div *ngIf="organizationForm.controls['companySize'].errors && organizationForm.controls['companySize'].touched"
                                    class="text-danger">
                                    <small>{{"*Company Size is required" | titlecase}}</small>
                                </div>
                            </div>

                            <div class="form-field flex flex-col gap-2 flex-1 w-full sm:w-auto">
                                <label for="contact">Phone</label>
                                <app-phone-input [readOnly]="nonEditable" formControlName="contact" appendTo="body"></app-phone-input>
                                <div *ngIf="organizationForm.controls['contact'].errors && organizationForm.controls['contact'].touched"
                                    class="text-danger">
                                    <small>{{"*Phone is not valid" | titlecase}}</small>
                                </div>
                            </div>
                        </div>

                        <div class="form-field flex flex-col gap-2">
                            <label for="address">Address</label>
                            <textarea rows="3" id="address" formControlName="address"
                                class="w-full p-2 rounded-base border border-gray-300 focus:border-orange-500 focus:ring-orange-500 focus:outline-none resize-none"
                                placeholder="Address"></textarea>
                            <div *ngIf="organizationForm.controls['address'].errors && organizationForm.controls['address'].touched"
                                class="text-danger">
                                <small>{{"*Address is Required" | titlecase}}</small>
                            </div>
                        </div>

                        <div class="form-field flex flex-col gap-2">
                            <label for="email">Company Email <span class="text-danger">*</span></label>
                            <app-input type="email" formControlName="email" placeholder="Info@yourcompanyemail.com" />
                            <div *ngIf="organizationForm.controls['email'].errors && organizationForm.controls['email'].touched"
                                class="text-danger">
                                <small>{{"*Company Email is Required" | titlecase}}</small>
                            </div>
                        </div>

                        <div class="form-field flex flex-col gap-2">
                            <label for="domain">Company Domain <span class="text-danger">*</span></label>
                            <app-input type="text" formControlName="domain" [readonly]="true"
                                placeholder="https://yourcompanydomain.com" />
                            <div *ngIf="organizationForm.controls['domain'].errors && organizationForm.controls['domain'].touched"
                                class="text-danger">
                                <small>{{"*Company Domain is Required" | titlecase}}</small>
                            </div>
                        </div>

                        <div *ngIf="!nonEditable" class="button-group flex-shrink-0 mt-auto w-full flex">
                            <app-button variant="filled" severity="primary" (clicked)="validateForm()"
                                [disabled]="isSubmitting || !organizationForm.valid" [fullWidth]="true"
                                [class]="'w-full justify-center text-center'"
                                [label]="isSubmitting ? 'Saving...' : 'Save Changes'" />
                        </div>
                    </form>
                </p-card>
            </div>
            @if(canView()){
            <div class="flex-1 xl:w-1/2 flex flex-col gap-3">
                @if(currentUser?.role === USER_ROLE.SUPER_ADMIN){
                <p-card class="tag-type-card border rounded-base flex-shrink-0">
                    <div class="flex-1 flex flex-col gap-4">
                        <div class="font-bold text-lg">People login</div>
                        <div class="flex flex-col gap-2">
                            @for(method of loginMethods; track method.value){
                            <div class="flex items-center justify-between p-2 border rounded-base bg-white">
                                <div class="flex items-center gap-3 flex-1">
                                    @if(method.icon){
                                    <img [src]="method.icon" alt="{{method.label}}" class="w-4 h-4">
                                    }
                                    <span class="text-sm">{{method.label}}</span>
                                </div>
                                <app-toggle [(ngModel)]="method.enabled"
                                    (onChange)="onLoginMethodToggle(method, $event)">
                                </app-toggle>
                            </div>
                            }
                        </div>
                    </div>
                </p-card>
                }
                @if(isSystemAdmin()){
                <p-card class="tag-type-card border rounded-base flex-shrink-0">
                    <div class="flex-1 flex flex-col gap-3">
                        <div>
                            <div class="font-bold text-lg">Account Owner</div>
                            <p class="text-sm text-gray-500 mt-1">You are the current account owner. You can transfer
                                this role to another user.</p>
                        </div>
                        <app-button variant="outlined" severity="contrast" icon="arrow-left-right" iconSource="lucide"
                            label="Transfer account owner" (clicked)="openTransferDialog()" />
                    </div>
                </p-card>
                }
            </div>
            }
        </div>
    </div>
</div>

<app-dialog [(visible)]="showTransferSystemAdminDialog" title="Transfer Account Owner"
    description="Select a user to transfer account owner (system admin) role to." class="max-w-lg">
    <ng-template #bodyTemplate>
        <div class="flex flex-col gap-4 p-1">
            <input type="text" [(ngModel)]="transferUserSearchQuery" (input)="onTransferUserSearch()"
                placeholder="Search by name or email"
                class="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-orange-500" />
            <div class="flex flex-col gap-1 max-h-64 overflow-y-auto">
                @for(user of filteredTransferUsers; track user._id){
                <div class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 border"
                    [class.border-orange-500]="selectedTransferUser?._id === user._id"
                    [class.bg-orange-50]="selectedTransferUser?._id === user._id"
                    [class.border-transparent]="selectedTransferUser?._id !== user._id"
                    (click)="selectedTransferUser = user">
                    <img [src]="user | userAvatar" class="rounded-full w-8 h-8 object-cover" alt="" />
                    <div class="flex flex-col flex-1 min-w-0">
                        <span class="text-sm font-medium truncate">{{ user | fullName }}</span>
                        <span class="text-xs text-gray-500 truncate">{{ user.email }}</span>
                    </div>
                    @if(selectedTransferUser?._id === user._id){
                    <app-icon type="check" source="lucide" class="text-orange-500" />
                    }
                </div>
                } @empty {
                <div class="text-sm text-gray-500 text-center py-4">No users found</div>
                }
            </div>
        </div>
    </ng-template>
    <ng-template #footerTemplate>
        <div class="flex justify-end gap-2">
            <app-button variant="outlined" severity="contrast" label="Cancel"
                (clicked)="showTransferSystemAdminDialog = false" />
            <app-button variant="filled" severity="primary" label="Transfer"
                [disabled]="!selectedTransferUser || isTransferringSystemAdmin"
                (clicked)="confirmTransferSystemAdmin()" />
        </div>
    </ng-template>
</app-dialog>